import React from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../auth/AuthContext';
import { useAppUi } from '../app/useAppUi';
import logoUrl from '../assets/logo.png';

const drawerWidth = 224;

// Groups are separated by a gap only; no headings, to keep the sidebar quiet.
const NAV = [
  [
    { to: '/dashboard', labelKey: 'nav.dashboard', Icon: SpaceDashboardOutlinedIcon },
    { to: '/ai-usage', labelKey: 'nav.aiUsage', Icon: AutoAwesomeOutlinedIcon },
    { to: '/partner-analytics', labelKey: 'nav.partnerAnalytics', Icon: InsightsOutlinedIcon },
  ],
  [
    { to: '/agents', labelKey: 'nav.agents', Icon: BadgeOutlinedIcon },
    { to: '/subscriptions', labelKey: 'nav.subscriptions', Icon: WorkspacePremiumOutlinedIcon },
    { to: '/users', labelKey: 'nav.users', Icon: PeopleOutlineIcon },
    { to: '/bot-users', labelKey: 'nav.botUsers', Icon: SmartToyOutlinedIcon },
  ],
  [
    { to: '/curated-tours', labelKey: 'nav.curatedTours', Icon: ExploreOutlinedIcon },
    { to: '/partners', labelKey: 'nav.partners', Icon: HandshakeOutlinedIcon },
    { to: '/providers', labelKey: 'nav.providers', Icon: HubOutlinedIcon },
  ],
  [
    { to: '/search-history', labelKey: 'nav.searchHistory', Icon: ManageSearchOutlinedIcon },
    { to: '/broadcast', labelKey: 'nav.broadcast', Icon: CampaignOutlinedIcon },
    { to: '/admins', labelKey: 'nav.admins', Icon: AdminPanelSettingsOutlinedIcon },
  ],
];

const ALL_ITEMS = NAV.flat();

export function AdminLayout() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, themeMode, setThemeMode, locale, setLocale } = useAppUi();

  const current = ALL_ITEMS.find((i) => location.pathname.startsWith(i.to));
  const username = admin?.username || 'admin';

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2.5, pt: 2.25, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box component="img" src={logoUrl} alt="" sx={{ width: 32, height: 32, borderRadius: 1.25, objectFit: 'cover' }} />
        <Typography sx={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.01em' }}>
          {t('layout.appName')}
        </Typography>
      </Box>

      <Box component="nav" sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 2 }}>
        {NAV.map((group, gi) => (
          <List key={gi} disablePadding sx={{ mt: gi ? 1.5 : 0.5 }}>
            {group.map(({ to, labelKey, Icon }) => (
              <ListItemButton
                key={to}
                component={NavLink}
                to={to}
                onClick={() => setMobileOpen(false)}
                sx={{
                  borderRadius: 1.25,
                  py: 0.55,
                  px: 1.25,
                  mb: 0.125,
                  color: 'text.secondary',
                  '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 30 },
                  '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                  '&.active': { bgcolor: 'action.selected', color: 'primary.main' },
                  '&.active .MuiListItemText-primary': { fontWeight: 600 },
                }}
              >
                <ListItemIcon>
                  <Icon sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText primary={t(labelKey)} slotProps={{ primary: { sx: { fontSize: 13.5, fontWeight: 500 } } }} />
              </ListItemButton>
            ))}
          </List>
        ))}
      </Box>

      <Box
        sx={{
          mx: 1.5,
          mb: 1.5,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Avatar sx={{ width: 28, height: 28, fontSize: 12, fontWeight: 700, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
          {username.slice(0, 1).toUpperCase()}
        </Avatar>
        <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, ml: 0.5 }}>
          {username}
        </Typography>
        <Tooltip title={t('nav.language')}>
          <IconButton
            size="small"
            onClick={() => setLocale(locale === 'en' ? 'ru' : 'en')}
            sx={{ fontSize: 11.5, fontWeight: 700, width: 30, height: 30, color: 'text.secondary' }}
          >
            {locale === 'en' ? 'EN' : 'RU'}
          </IconButton>
        </Tooltip>
        <Tooltip title={themeMode === 'dark' ? t('theme.light') : t('theme.dark')}>
          <IconButton size="small" onClick={() => setThemeMode(themeMode === 'dark' ? 'light' : 'dark')} sx={{ color: 'text.secondary' }}>
            {themeMode === 'dark' ? <LightModeOutlinedIcon sx={{ fontSize: 18 }} /> : <DarkModeOutlinedIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title={t('nav.logout')}>
          <IconButton
            size="small"
            aria-label={t('nav.logout')}
            sx={{ color: 'text.secondary' }}
            onClick={() => {
              logout();
              navigate('/login', { replace: true });
            }}
          >
            <LogoutIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  const paperSx = {
    width: drawerWidth,
    boxSizing: 'border-box',
    bgcolor: 'background.paper',
    borderRight: '1px solid',
    borderColor: 'divider',
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { md: 'none' },
          bgcolor: 'background.default',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar variant="dense" sx={{ gap: 1 }}>
          <IconButton edge="start" onClick={() => setMobileOpen(true)} aria-label="menu">
            <MenuIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 600, fontSize: 15 }} noWrap>
            {current ? t(current.labelKey) : t('layout.appName')}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box component="aside" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': paperSx }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': paperSx }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, sm: 3, lg: 5 },
          pt: { xs: 9, md: 4 },
          pb: 6,
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
